/**
 * CollectionListPage — full-page listing of a collection's entries.
 *
 * Route: /c/:typeSlug
 *
 * Fetches entries from GET /customer/portal/collections/:typeSlug
 * and renders a paginated grid of cards, each linking to /c/:typeSlug/:slug.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, LayoutList, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { useBranding } from '../contexts/BrandingContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CollectionEntry {
  id: string;
  title: string;
  slug?: string | null;
  dataJson: Record<string, unknown>;
  publishedAt: string | null;
}

interface CollectionListResponse {
  entries: CollectionEntry[];
  total?: number;
  contentType?: {
    id: string;
    name: string;
    slug: string;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getTenantSlug(): string {
  const envSlug = import.meta.env.VITE_TENANT_SLUG as string | undefined;
  if (envSlug) return envSlug;
  const host = window.location.hostname;
  const parts = host.split('.');
  if (parts.length >= 2 && parts[0] !== 'localhost') return parts[0];
  return 'tenant1';
}

const PAGE_SIZE = 12;

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CollectionListPage() {
  const { typeSlug } = useParams<{ typeSlug: string }>();
  const navigate = useNavigate();
  const branding = useBranding();

  const [entries, setEntries] = useState<CollectionEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [collectionName, setCollectionName] = useState<string>('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Reset to page 0 whenever the collection changes
  useEffect(() => {
    setPage(0);
    setCollectionName('');
    setEntries([]);
    setTotal(0);
  }, [typeSlug]);

  useEffect(() => {
    if (!typeSlug) return;
    setLoading(true);
    setError(false);
    setNotFound(false);

    let cancelled = false;
    const offset = page * PAGE_SIZE;
    const headers: Record<string, string> = { 'x-tenant-slug': getTenantSlug() };
    if (branding.defaultSpaceId) headers['x-space-id'] = branding.defaultSpaceId;
    fetch(
      `/customer/portal/collections/${encodeURIComponent(typeSlug)}?limit=${PAGE_SIZE}&offset=${offset}`,
      { headers },
    )
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) throw new Error('Failed to load');
        const data = (await res.json()) as CollectionListResponse;
        if (cancelled) return;
        setEntries(data.entries ?? []);
        setTotal(data.total ?? (data.entries ?? []).length);
        if (data.contentType?.name) {
          setCollectionName(data.contentType.name);
          document.title = `${data.contentType.name} — ${branding.name}`;
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [typeSlug, page, branding.name]);

  // Restore title on unmount only
  useEffect(() => {
    return () => {
      document.title = branding.name;
    };
  }, [branding.name]);

  // ── 404 ────────────────────────────────────────────────────────────────────
  if (notFound) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
          <LayoutList size={40} className="text-zinc-300" />
          <div>
            <h1 className="text-xl font-semibold text-zinc-800">Collection not found</h1>
            <p className="text-sm text-zinc-500 mt-1">
              This collection doesn&apos;t exist or has no published entries.
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-2 flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            <ArrowLeft size={14} />
            Back to home
          </button>
        </div>
      </AppLayout>
    );
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <AppLayout>
      {/* Header */}
      <div className="mb-6 space-y-1">
        {collectionName && <h1 className="text-2xl font-bold text-zinc-900">{collectionName}</h1>}
        {!loading && total > 0 && (
          <p className="text-sm text-zinc-400">
            {total} {total === 1 ? 'entry' : 'entries'}
          </p>
        )}
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 size={24} className="animate-spin text-zinc-400" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-24 text-center gap-3">
          <LayoutList size={32} className="text-zinc-300" />
          <p className="text-sm text-zinc-500">Failed to load entries. Please try again.</p>
        </div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center gap-3">
          <LayoutList size={32} className="text-zinc-300" />
          <p className="text-sm text-zinc-500">No entries published yet.</p>
        </div>
      ) : (
        <>
          {/* Grid */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {entries.map((entry) => {
              const data = entry.dataJson ?? {};
              const imageUrl =
                (data.imageUrl as string | undefined) ??
                (data.image as string | undefined) ??
                (data.cover as string | undefined);
              const excerpt =
                (data.excerpt as string | undefined) ??
                (data.summary as string | undefined) ??
                (data.description as string | undefined);
              const entryPath = `/c/${encodeURIComponent(typeSlug!)}/${encodeURIComponent(entry.slug ?? entry.id)}`;

              return (
                <Link
                  key={entry.id}
                  to={entryPath}
                  className="block bg-white border border-zinc-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  {imageUrl && (
                    <div className="aspect-video overflow-hidden bg-zinc-100">
                      <img
                        src={imageUrl}
                        alt={entry.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-4 space-y-1.5">
                    <h3 className="font-semibold text-zinc-900 text-sm leading-snug line-clamp-2">
                      {entry.title}
                    </h3>
                    {excerpt && <p className="text-xs text-zinc-500 line-clamp-3">{excerpt}</p>}
                    {entry.publishedAt && (
                      <p className="text-[10px] text-zinc-400">
                        {new Date(entry.publishedAt).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-2 rounded-lg border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-zinc-500">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="p-2 rounded-lg border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
}
